import { FC, memo, useCallback, useEffect, useState } from 'react';

import { MagritteNotificationName } from 'utils/constants/notification';

import {
    ActionBar,
    BottomSheet,
    Button,
    Cell,
    CellText,
    CheckableChip,
    Checkbox,
    ChipsContainer,
    FormLabel,
    HSpacingContainer,
    Input,
    Modal,
    NavBar,
    NumberInput,
    Text,
    VSpacing,
    VSpacingContainer,
} from '@hh.ru/magritte-ui';
import { CrossOutlinedSize24, MinusOutlinedSize24, PlusOutlinedSize24 } from '@hh.ru/magritte-ui/icon';

import { createPromoCode } from 'components/Admin/PromoCodes/api/createPromoCode';
import { updatePromoCodeApi } from 'components/Admin/PromoCodes/api/updatePromoCode';
import {
    getSelectedSegmentsFromDTO,
    getSelectedSegmentsToDTO,
    segmentsMap,
} from 'components/Admin/PromoCodes/utils/mappingData';
import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { Period } from 'components/common/Period/Period';

import { addMagritteNotification } from 'store/models/notification';
import { PromoCode, PromoCodeBound } from 'store/models/promoCodes';
import { useDispatch } from 'store/utils';

import styles from 'components/Admin/PromoCodes/PromoCodeModal/PromoCodeModal.module.less';

interface PromoCodeModalProps {
    visible: boolean;
    onClose: () => void;
    editPromoCode?: PromoCode | null;
}

const DEFAULT_ACTIVATION_LIMIT = 100;
const ACTIVATION_STEP = 100;

const ALL_SEGMENTS = [...segmentsMap.keys()];

interface FormState {
    promoCode: string;
    description: string;
    bonusSum: string;
    startDate: string | null;
    endDate: string | null;
    activationsLimit: number;
    isUnlimitedActivations: boolean;
    employerIds: string;
    selectedSegments: Set<string>;
}

interface FormErrors {
    promoCode: boolean;
    description: boolean;
    bonusSum: boolean;
}

const INITIAL_FORM_STATE: FormState = {
    promoCode: '',
    description: '',
    bonusSum: '',
    startDate: null,
    endDate: null,
    activationsLimit: DEFAULT_ACTIVATION_LIMIT,
    isUnlimitedActivations: false,
    employerIds: '',
    selectedSegments: new Set(),
};

const INITIAL_FORM_ERRORS: FormErrors = {
    promoCode: false,
    description: false,
    bonusSum: false,
};

const FieldErrors = {
    promoCode: 'Укажите название',
    description: 'Укажите описание',
    bonusSum: 'Укажите сумму',
};

export const MAX_PROMO_CODE_LENGTH = 255;
export const PROMO_CODE_ALLOWED_CHARACTERS_REGEX = /^[a-zA-Z0-9_]*$/;
export const EMPLOYERS_ALLOWED_CHARACTERS_REGEX = /^[0-9,\s]*$/;
export const POSITIVE_NUMBERS_REGEX = /^[0-9]*$/;

export const PromoCodeModalComponent: FC<PromoCodeModalProps> = ({ visible, onClose, editPromoCode }) => {
    const dispatch = useDispatch();
    const isEdit = Boolean(editPromoCode);

    const [formState, setFormState] = useState<FormState>(INITIAL_FORM_STATE);
    const [formErrors, setFormErrors] = useState<FormErrors>(INITIAL_FORM_ERRORS);
    const [isLoading, setIsLoading] = useState(false);

    const resetForm = useCallback(() => {
        setFormState(INITIAL_FORM_STATE);
        setFormErrors(INITIAL_FORM_ERRORS);
    }, []);

    // Инициализация данных при редактировании
    useEffect(() => {
        if (editPromoCode) {
            setFormState({
                promoCode: editPromoCode.promoCode,
                description: editPromoCode.description,
                bonusSum: String(editPromoCode.bonus.sum),
                startDate: editPromoCode.startDate,
                endDate: editPromoCode.endDate,
                activationsLimit: editPromoCode.activationsLimit || DEFAULT_ACTIVATION_LIMIT,
                isUnlimitedActivations: editPromoCode.activationsLimit === null,
                employerIds: editPromoCode.bound?.employerIds?.join(', ') || '',
                selectedSegments: getSelectedSegmentsFromDTO(editPromoCode.bound?.employerMarketSegments),
            });
        }
    }, [editPromoCode]);

    const handleCloseModal = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleFieldChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
        setFormState((prev) => ({ ...prev, [field]: value }));

        if (field in INITIAL_FORM_ERRORS) {
            setFormErrors((prev) => ({ ...prev, [field]: false }));
        }
    }, []);

    const handleIncrementActivations = useCallback(() => {
        setFormState((prev) => ({
            ...prev,
            activationsLimit: prev.activationsLimit + ACTIVATION_STEP,
        }));
    }, []);

    const handleDecrementActivations = useCallback(() => {
        setFormState((prev) => ({
            ...prev,
            activationsLimit: Math.max(0, prev.activationsLimit - ACTIVATION_STEP),
        }));
    }, []);

    const handleToggleUnlimited = useCallback(() => {
        setFormState((prev) => ({
            ...prev,
            isUnlimitedActivations: !prev.isUnlimitedActivations,
        }));
        setFormErrors((prev) => ({ ...prev, activationsLimit: false }));
    }, []);

    const handleToggleSegment = useCallback((segment: string) => {
        setFormState((prev) => {
            const newSegments = new Set(prev.selectedSegments);
            if (segment !== 'Все' && newSegments.has('Все')) {
                newSegments.delete('Все');
            }
            if (segment === 'Все' && !newSegments.has(segment)) {
                newSegments.clear();
            }
            if (newSegments.has(segment)) {
                newSegments.delete(segment);
            } else {
                newSegments.add(segment);
            }
            return { ...prev, selectedSegments: newSegments };
        });
    }, []);

    const validateForm = useCallback((): boolean => {
        const errors: FormErrors = {
            promoCode: !formState.promoCode.trim(),
            description: !formState.description.trim(),
            bonusSum:
                !formState.bonusSum.trim() || isNaN(Number(formState.bonusSum)) || Number(formState.bonusSum) <= 0,
        };

        setFormErrors(errors);

        return !Object.values(errors).some((error) => error);
    }, [formState]);

    const handleSave = useCallback(async () => {
        if (!validateForm()) {
            dispatch(addMagritteNotification(MagritteNotificationName.AdminCreateOrUpdatePromoCodeValidationError));
            return;
        }

        const parsedEmployerIds = formState.employerIds
            .split(',')
            .map((id) => parseInt(id.trim(), 10))
            .filter((id) => !isNaN(id));

        const employerIds: number[] = [...parsedEmployerIds];
        const employerMarketSegments = getSelectedSegmentsToDTO(formState.selectedSegments);

        const bound =
            employerIds.length || employerMarketSegments?.length
                ? ({
                      employerIds: employerIds?.length > 0 ? employerIds : null,
                      employerMarketSegments:
                          employerMarketSegments && employerMarketSegments.length > 0 ? employerMarketSegments : null,
                  } as PromoCodeBound)
                : null;

        const bonus = {
            sum: Number(formState.bonusSum),
        };

        const body = {
            promoCode: formState.promoCode.trim(),
            description: formState.description.trim(),
            startDate: formState.startDate,
            endDate: formState.endDate,
            activationsLimit: formState.isUnlimitedActivations ? null : formState.activationsLimit,
            bound,
        };

        if (isEdit) {
            await dispatch(updatePromoCodeApi(body, editPromoCode?.bound || null, setIsLoading, handleCloseModal));
        } else {
            await dispatch(createPromoCode({ ...body, bonus }, setIsLoading, handleCloseModal));
        }
    }, [
        validateForm,
        formState.employerIds,
        formState.selectedSegments,
        formState.bonusSum,
        formState.promoCode,
        formState.description,
        formState.startDate,
        formState.endDate,
        formState.isUnlimitedActivations,
        formState.activationsLimit,
        isEdit,
        handleCloseModal,
        dispatch,
        editPromoCode?.bound,
    ]);

    const content = (
        <>
            <VSpacingContainer default={24}>
                <VSpacingContainer default={12}>
                    <FormLabel>Промокод</FormLabel>
                    <Input
                        size="medium"
                        placeholder="Например, CLM25"
                        value={formState.promoCode}
                        onChange={(value) => {
                            if (PROMO_CODE_ALLOWED_CHARACTERS_REGEX.test(value)) {
                                handleFieldChange('promoCode', value);
                            }
                        }}
                        clearableOnFocus
                        disabled={isEdit}
                        invalid={formErrors.promoCode}
                        errorMessage={formErrors.promoCode && FieldErrors.promoCode}
                        maxLength={MAX_PROMO_CODE_LENGTH}
                    />
                </VSpacingContainer>

                <VSpacingContainer default={12}>
                    <FormLabel>Описание</FormLabel>
                    <Input
                        size="medium"
                        placeholder="Например «Промокод на получение 1000 бонусов»"
                        value={formState.description}
                        onChange={(value) => handleFieldChange('description', value)}
                        clearableOnFocus
                        invalid={formErrors.description}
                        errorMessage={formErrors.description && FieldErrors.description}
                    />
                </VSpacingContainer>

                <VSpacingContainer default={12}>
                    <FormLabel>Сумма бонусов</FormLabel>
                    <NumberInput
                        size="medium"
                        placeholder="Укажите количество"
                        value={formState.bonusSum}
                        onChange={(value) => {
                            if (POSITIVE_NUMBERS_REGEX.test(value)) {
                                handleFieldChange('bonusSum', value);
                            }
                        }}
                        disabled={isEdit}
                        clearableOnFocus
                        invalid={formErrors.bonusSum}
                        errorMessage={formErrors.bonusSum && FieldErrors.bonusSum}
                    />
                </VSpacingContainer>

                <Period
                    dateStart={{
                        placeholder: 'Дата старта',
                        selectedDate: formState.startDate ? new Date(formState.startDate) : undefined,
                        onDateSelect: (value) => handleFieldChange('startDate', value),
                        placement: 'bottom-left',
                    }}
                    dateEnd={{
                        placeholder: 'Дата остановки',
                        selectedDate: formState.endDate ? new Date(formState.endDate) : undefined,
                        onDateSelect: (value) => handleFieldChange('endDate', value),
                        placement: 'bottom-left',
                    }}
                    title="Срок действия"
                />

                <VSpacingContainer default={24}>
                    <VSpacingContainer default={12}>
                        <FormLabel>Работодатель</FormLabel>
                        <Input
                            size="large"
                            placeholder="Employer ID через запятую, необязательно"
                            value={formState.employerIds}
                            onChange={(value) => {
                                if (EMPLOYERS_ALLOWED_CHARACTERS_REGEX.test(value)) {
                                    handleFieldChange('employerIds', value);
                                }
                            }}
                            clearableOnFocus
                        />
                    </VSpacingContainer>
                    <VSpacingContainer default={12}>
                        <FormLabel>Сегмент</FormLabel>
                        <ChipsContainer>
                            {ALL_SEGMENTS.map((segment) => (
                                <CheckableChip
                                    key={segment}
                                    type="checkbox"
                                    checked={formState.selectedSegments.has(segment)}
                                    mode="primary"
                                    size="small"
                                    style="neutral"
                                    onChange={() => handleToggleSegment(segment)}
                                >
                                    {segment}
                                </CheckableChip>
                            ))}
                        </ChipsContainer>
                    </VSpacingContainer>
                </VSpacingContainer>

                <VSpacingContainer default={12}>
                    <FormLabel>Количество активаций промокода</FormLabel>
                    <HSpacingContainer default={12}>
                        <NumberInput
                            size="medium"
                            value={String(formState.activationsLimit)}
                            onChange={(value) => {
                                if (POSITIVE_NUMBERS_REGEX.test(value)) {
                                    handleFieldChange('activationsLimit', Number(value));
                                }
                            }}
                            disabled={formState.isUnlimitedActivations}
                            clearableOnFocus
                        />
                        <Button
                            size="medium"
                            mode="secondary"
                            style="neutral"
                            icon={<MinusOutlinedSize24 />}
                            onClick={handleDecrementActivations}
                            disabled={formState.isUnlimitedActivations}
                        />
                        <Button
                            size="medium"
                            mode="secondary"
                            style="neutral"
                            icon={<PlusOutlinedSize24 />}
                            onClick={handleIncrementActivations}
                            disabled={formState.isUnlimitedActivations}
                        />
                        <Cell
                            align="center"
                            onClick={handleToggleUnlimited}
                            left={
                                <div className={styles.unlimitedActivations}>
                                    <Checkbox checked={formState.isUnlimitedActivations} readOnly />
                                </div>
                            }
                        >
                            <CellText>Без ограничений</CellText>
                        </Cell>
                    </HSpacingContainer>
                </VSpacingContainer>
            </VSpacingContainer>
            <VSpacing default={32} />
        </>
    );

    const actions = (
        <ActionBar
            secondaryActions={
                <Button mode="secondary" style="accent" size="medium" onClick={handleCloseModal} disabled={isLoading}>
                    Отмена
                </Button>
            }
            primaryActions={
                <Button
                    mode="primary"
                    style="accent"
                    size="medium"
                    onClick={handleSave}
                    disabled={isLoading}
                    loading={isLoading}
                >
                    Сохранить
                </Button>
            }
        />
    );

    return (
        <>
            <Modal
                size="large"
                visible={visible}
                header={
                    <MagritteModalHeader
                        title={isEdit ? 'Редактирование промокода' : 'Новый промокод'}
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={handleCloseModal} />}
                    />
                }
                onClose={handleCloseModal}
                footer={actions}
                onAfterExit={resetForm}
            >
                {content}
            </Modal>
            <BottomSheet
                height="full-screen"
                visible={visible}
                onClose={handleCloseModal}
                onAfterExit={resetForm}
                header={
                    <HSpacingContainer default={12}>
                        <NavBar>
                            <NavBar.Pane>
                                <Text typography="title-4-semibold">
                                    {isEdit ? 'Редактирование промокода' : 'Новый промокод'}
                                </Text>
                            </NavBar.Pane>
                        </NavBar>
                    </HSpacingContainer>
                }
                footer={actions}
            >
                {content}
            </BottomSheet>
        </>
    );
};

export const PromoCodeModal = memo(PromoCodeModalComponent);
